import { useQueryClient } from '@tanstack/react-query'
import { Button, Flex, Form, Input, Modal } from 'antd'
import { ModalProps } from 'antd/lib'
import { useEffect } from 'react'

import {
  useStocksServiceCreateFormEndpoint,
  useStocksServiceGetFormEndpoint,
  useStocksServiceGetFormListEndpointKey,
  useStocksServiceUpdateFormEndpoint
} from 'src/api/queries'
import { ApiError } from 'src/api/requests'
import { requiredRules } from 'src/app-constants'
import { message, SaveConfirm } from 'src/components'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

type Props = {
  id?: string
  onCancel: () => void
} & ModalProps

const getModalText = (id?: string) => {
  return {
    title: `medicine_register.pharmaceutical_form.${id ? 'edit' : 'add'}_form`,
    button: `common.${id ? 'edit' : 'add'}`
  }
}

export const PharmaceuticalFormModal = ({ id, onCancel, ...props }: Props) => {
  const { t } = useLocales()
  const [form] = Form.useForm()

  const queryClient = useQueryClient()

  const { title, button } = getModalText(id)

  const { data } = useStocksServiceGetFormEndpoint({ id: String(id) }, [id], {
    enabled: !!id
  })

  const { mutate: edit, isPending: isEditing } =
    useStocksServiceUpdateFormEndpoint({
      onError: (e: ApiError) => formErrorHandler(e.body.errors),
      onSuccess: () => onSuccesMutation()
    })

  const { mutate: create, isPending: isCreating } =
    useStocksServiceCreateFormEndpoint({
      onError: (e: ApiError) => formErrorHandler(e.body.errors),
      onSuccess: () => onSuccesMutation()
    })

  const onSubmit = async () => {
    const values = await form.validateFields()

    const requestBody = {
      name: values.name
    }

    id ? edit({ requestBody, id }) : create({ requestBody })
  }

  const onSuccesMutation = () => {
    queryClient.invalidateQueries({
      queryKey: [useStocksServiceGetFormListEndpointKey]
    })
    message.success(t('common.save_success'))
    onCancel()
  }

  useEffect(() => {
    if (data && id) {
      form.setFieldsValue({ name: data.name })
    }
  }, [data, id])

  return (
    <Modal
      open
      title={t(title)}
      centered
      footer={null}
      maskClosable={false}
      onCancel={onCancel}
      afterClose={form.resetFields}
      {...props}
    >
      <Flex vertical gap={24}>
        <Form requiredMark={false} layout="vertical" form={form}>
          <Form.Item name="name" rules={requiredRules}>
            <Input
              placeholder={t('medicine_register.pharmaceutical_form.add_form')}
            />
          </Form.Item>
        </Form>
        <Flex gap={16} justify="center">
          <Button onClick={onCancel}>{t('common.cancel')}</Button>

          <SaveConfirm
            title={`${t(button)}?`}
            onConfirm={onSubmit}
            okButtonProps={{ loading: isCreating || isEditing }}
          >
            <Button type="primary">{t(button)}</Button>
          </SaveConfirm>
        </Flex>
      </Flex>
    </Modal>
  )
}
